import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelAppRun,
  createAppRun,
  deleteAppRun,
  getAppRun,
  listApps,
  rerunAppRun,
} from '@api/apps';

export const appKeys = {
  catalog: ['apps', 'catalog'] as const,
  run: (runId: string) => ['apps', 'run', runId] as const,
};

const CONVERSATIONS_KEY = ['conversations'] as const;
const RUN_POLL_MS = 15_000;
const STOP_POLL_MS = 2_000;

export const useAppCatalog = (enabled = true) =>
  useQuery({
    queryKey: appKeys.catalog,
    queryFn: listApps,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

/**
 * The run as the server sees it — the source of truth for everything the screen
 * shows. Polled while a turn is in flight so a run whose stream was interrupted
 * still reaches its outcome on screen, and quickly while a stop waits for the
 * row to confirm it.
 *
 * <p>Polled from a hidden tab too: the app never refetches on focus, so a page
 * left in the background would otherwise come back showing a run long ended.
 */
export const useAppRun = (runId: string | undefined, stopPending = false) =>
  useQuery({
    queryKey: appKeys.run(runId ?? 'none'),
    queryFn: () => getAppRun(runId as string),
    enabled: Boolean(runId),
    refetchInterval: (query) => {
      if (query.state.data?.status !== 'RUNNING') return false;
      return stopPending ? STOP_POLL_MS : RUN_POLL_MS;
    },
    refetchIntervalInBackground: true,
  });

/**
 * The mutations that change a run without streaming a turn. Starting, refining
 * and answering are streams, so they live in {@code useAppRunStream}.
 */
interface RerunVariables {
  readonly runId: string;
  /** The source run's inputs to carry over; omitted means all of them. */
  readonly keepAttachmentIds?: readonly string[];
}

export const useAppRunActions = () => {
  const queryClient = useQueryClient();

  // A run lives in a conversation, which is what the sidebar lists.
  const refreshConversations = () => {
    queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY }).catch(() => undefined);
  };

  // Deliberately not seeded into the cache. A run is opened empty and its files
  // are uploaded straight after, so the row returned here is already out of date
  // by the time the screen for it mounts — and seeding it would have that screen
  // show a run with no inputs until something else happened to refetch it.
  const create = useMutation({
    mutationFn: (key: string) => createAppRun(key),
    onSuccess: refreshConversations,
  });

  const rerun = useMutation({
    mutationFn: ({ runId, keepAttachmentIds }: RerunVariables) =>
      rerunAppRun(runId, keepAttachmentIds),
    onSuccess: refreshConversations,
  });

  const cancel = useMutation({
    mutationFn: (runId: string) => cancelAppRun(runId),
    onSuccess: (_data, runId) => {
      queryClient.invalidateQueries({ queryKey: appKeys.run(runId) }).catch(() => undefined);
    },
  });

  const remove = useMutation({
    mutationFn: (runId: string) => deleteAppRun(runId),
    onSuccess: (_data, runId) => {
      queryClient.removeQueries({ queryKey: appKeys.run(runId) });
      refreshConversations();
    },
  });

  return {
    create: create.mutateAsync,
    isCreating: create.isPending,
    rerun: rerun.mutateAsync,
    cancel: cancel.mutateAsync,
    isCancelling: cancel.isPending,
    remove: remove.mutateAsync,
    isRemoving: remove.isPending,
  };
};
